import { useState, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV, PHONE, PHONE_TEL } from "../lib/data";
import Logo from "./Logo";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef(null);
  const location = useLocation();
  const isFinancePage = location.pathname.startsWith("/payment-options");
  // Trailing-slash URLs are common on the public site; keep the header legible on both forms.
  const normalizedPath = location.pathname.replace(/\/+$/, "") || "/";
  const isDealsPage = ["/aircon-deals", "/deals"].includes(normalizedPath);
  const isCleaningLanding = normalizedPath === "/split-system-cleaning";
  const isCleaningPage = ["/split-system-cleaning", "/secure-cleaning", "/colony-forming-units", "/aircon-colony-forming-units"].includes(normalizedPath);
  const solid = scrolled || open || isFinancePage || isCleaningPage;

  // The payment announcement is part of the fixed header. Keep its largest
  // measured height available to image-first landing pages so their artwork
  // never sits underneath the announcement (including on narrow mobiles).
  useEffect(() => {
    const root = document.documentElement;
    let tallestHeader = 0;
    const measure = () => {
      if (!headerRef.current) return;
      tallestHeader = Math.max(tallestHeader, Math.ceil(headerRef.current.getBoundingClientRect().height));
      root.style.setProperty("--sp-site-header-height", `${tallestHeader}px`);
    };
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (resizeObserver && headerRef.current) resizeObserver.observe(headerRef.current);
    const onResize = () => { tallestHeader = 0; measure(); };
    window.addEventListener("resize", onResize);
    measure();
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", onResize);
      root.style.removeProperty("--sp-site-header-height");
    };
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onResize = () => {
      if (window.innerWidth >= 1024) setOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <header
      ref={headerRef}
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-[9998] transition-all duration-300 ${
        solid ? "border-b border-[#E5E5EA] bg-[#F8F7F5]/98 py-2 lg:py-0 backdrop-blur-xl" : "py-3 lg:py-0"
      }`}
    >
      {!solid && <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/40 to-transparent" />}
      <nav className="sp-container relative flex items-center justify-between gap-6 lg:gap-14">
        <Logo onDark={!solid} scrolled={scrolled} />

        {!isCleaningLanding && <div className="hidden items-center gap-6 lg:flex lg:gap-10">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `link-line text-sm font-medium tracking-wide transition-colors duration-300 ${
                  solid
                    ? isActive ? "text-[#C8A46A]" : "text-[#1D1D1F] hover:text-[#C8A46A]"
                    : isActive ? "text-white" : "text-white/85 hover:text-white"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>}

        {!isCleaningLanding && <div className="flex items-center gap-2 sm:gap-3">
          {!isDealsPage && <NavLink
            to="/aircon-deals"
            data-testid="nav-deals-btn"
            className={() =>
              `inline-flex h-9 items-center justify-center rounded-full border px-3 text-[11px] font-bold uppercase tracking-[0.12em] transition-all sm:h-10 sm:px-4 sm:text-xs ${
                solid
                  ? "border-[#C8A46A] bg-white text-[#9A7540] hover:bg-[#C8A46A] hover:text-white"
                  : "border-white/60 bg-black/10 text-white hover:bg-white hover:text-[#1D1D1F]"
              }`
            }
          >
            Deals
          </NavLink>}
          <a
            href={PHONE_TEL}
            data-testid="nav-call-btn"
            className={`hidden items-center gap-2 text-sm font-semibold tracking-wide transition-colors duration-300 sm:flex ${solid ? "text-[#C8A46A]" : "text-white"}`}
          >
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((value) => !value)}
            className={`relative z-[10000] flex h-11 w-11 touch-manipulation items-center justify-center rounded-full border lg:hidden ${
              solid ? "border-[#E5E5EA] bg-white text-[#1D1D1F]" : "border-white/40 bg-black/10 text-white"
            }`}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-controls="mobile-navigation"
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>}
      </nav>



      {open && !isCleaningLanding && (
        <>
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 top-[72px] z-[9997] bg-black/20 lg:hidden"
            onClick={() => setOpen(false)}
          />
          <div
            id="mobile-navigation"
            data-testid="mobile-menu"
            className="fixed inset-x-0 top-[72px] z-[9999] max-h-[calc(100dvh-72px)] overflow-y-auto overscroll-contain border-t border-[#E5E5EA] bg-white shadow-2xl lg:hidden"
          >
            <div className="sp-container grid gap-0.5 py-4">
              {!isDealsPage && <NavLink
                to="/aircon-deals"
                data-testid="mobile-deals-link"
                onClick={() => setOpen(false)}
                className="mb-2 flex items-center justify-between rounded-xl border border-[#C8A46A]/40 bg-[#FBF7EF] px-4 py-3.5 font-serif text-lg text-[#1D1D1F]"
              >
                <span>Current Deals</span>
                <span className="font-sans text-xs font-bold uppercase tracking-[0.12em] text-[#9A7540]">Rinnai + Daikin</span>
              </NavLink>}
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `border-b border-[#F5F5F7] py-3.5 font-serif text-lg ${isActive ? "text-[#C8A46A]" : "text-[#1D1D1F]"}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <a
                href={PHONE_TEL}
                data-testid="mobile-call-btn"
                onClick={() => setOpen(false)}
                className="mt-4 flex items-center justify-center gap-2 rounded-full border border-[#C8A46A] py-3.5 text-sm font-semibold uppercase tracking-wider text-[#C8A46A]"
              >
                <Phone className="h-4 w-4" /> Call {PHONE}
              </a>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Navbar;
